import {
  AssetIdentifiers,
  AssetPermission,
  AssetType,
  PermissionName
} from '../types/asset-permissions.types'

const PERMISSION_RANK: Record<PermissionName, number> = {
  [PermissionName.OWNER]: 3,
  [PermissionName.EDITOR]: 2,
  [PermissionName.VIEWER]: 1
}

export function getHighestPermissionName(
  permissionA: PermissionName,
  permissionB: PermissionName
): PermissionName {
  return PERMISSION_RANK[permissionA] >= PERMISSION_RANK[permissionB] ? permissionA : permissionB
}

/** When folder and code permissions overlap, keep one row per entity with the highest permission. */
export function dedupeAssetPermissionsByHighest(permissions: AssetPermission[]): AssetPermission[] {
  const deduped = new Map<string, AssetPermission>()

  for (const permission of permissions) {
    if (!permission?.entityId) continue

    const existing = deduped.get(permission.entityId)
    if (!existing) {
      deduped.set(permission.entityId, permission)
      continue
    }

    deduped.set(permission.entityId, {
      ...existing,
      entityName: existing.entityName || permission.entityName,
      permissionName: getHighestPermissionName(existing.permissionName, permission.permissionName)
    })
  }

  return Array.from(deduped.values())
}

export function getAssetIdFromIdentifiers(assetIdentifiers: AssetIdentifiers): string | null {
  const { isBatch, batchId, directoryId } = assetIdentifiers
  const assetId = (isBatch ? batchId : directoryId) as string
  const assetType = isBatch ? AssetType.BATCH : AssetType.DIRECTORY
  return !assetId ? null : `${assetType}:${assetId}`
}

export function getAssetIdentifiersFromAssetId(
  assetId: string,
  isWhitelabel?: boolean,
  isCodeSubdomain?: boolean
): AssetIdentifiers {
  if (isWhitelabel) {
    return {
      subdomainId: assetId,
      isBatch: isCodeSubdomain ?? false
    }
  }
  if (!assetId?.includes(':')) {
    return {
      pageId: assetId,
      isBatch: false
    }
  }

  const assetIdParts = assetId?.split(':')

  const type = assetIdParts?.[0]
  const id = assetIdParts?.[1]

  if (!type || !id) return { isBatch: false }

  if (type === 'directory') {
    return {
      directoryId: id,
      isBatch: false
    }
  }

  if (type === AssetType.PAGE_TEMPLATE) {
    return {
      pageTemplateId: id,
      isBatch: false
    }
  }

  if (type === AssetType.PAGEV2) {
    return {
      isPageV2: true,
      isBatch: false
    }
  }

  return {
    batchId: id,
    isBatch: true
  }
}
